
import styles from '../style.module.css';
import { motion, useAnimation, useReducedMotion, useMotionValue, useTransform, animate } from "framer-motion";
import { useEffect, useState } from "react";

function parseStatValue(value) {
    const raw = String(value ?? "");
    const match = raw.match(/^(\D*)(\d+(?:[.,]\d+)?)(.*)$/);
    if (!match) {
        return { prefix: "", number: null, suffix: raw };
    }
    return {
        prefix: match[1] || "",
        number: Number(String(match[2]).replace(",", ".")),
        suffix: match[3] || "",
    };
}

export default function BannerRotateItem({
    value,
    text,
    background,
    valueColor,
    textColor,
    rotate,
    left = 0,
    delay = 0,
    index = 0,
}) {
    const reduceMotion = useReducedMotion();
    const controls = useAnimation();
    const { prefix, number, suffix } = parseStatValue(value);
    const count = useMotionValue(number ?? 0);
    const rounded = useTransform(count, (latest) => String(Math.round(latest)));
    const [displayValue, setDisplayValue] = useState(number == null ? String(value ?? "") : "0");

    const startX = index === 0 ? -90 : index === 1 ? 12 : 96;
    const spin = rotate + (rotate >= 0 ? 92 : -92);

    useEffect(() => {
        if (number == null) {
            setDisplayValue(String(value ?? ""));
            return undefined;
        }

        const unsubscribe = rounded.on("change", (latest) => {
            setDisplayValue(`${prefix}${latest}${suffix}`);
        });

        return unsubscribe;
    }, [number, prefix, rounded, suffix, value]);

    useEffect(() => {
        let cancelled = false;
        let countControls;

        const run = async () => {
            if (reduceMotion) {
                await controls.set({
                    y: 0,
                    x: 0,
                    rotate,
                    opacity: 1,
                    scale: 1,
                });
                if (number != null) {
                    count.set(number);
                    setDisplayValue(`${prefix}${Math.round(number)}${suffix}`);
                }
                return;
            }

            await controls.start({
                y: 0,
                x: 0,
                rotate,
                opacity: 1,
                scale: 1,
                transition: {
                    delay,
                    y: {
                        type: "spring",
                        stiffness: 240,
                        damping: 15,
                        mass: 0.85 + index * 0.16,
                    },
                    x: {
                        type: "spring",
                        stiffness: 150,
                        damping: 14,
                        mass: 0.9,
                    },
                    rotate: {
                        type: "spring",
                        stiffness: 100,
                        damping: 10,
                        mass: 1.05,
                    },
                    scale: {
                        type: "spring",
                        stiffness: 210,
                        damping: 12,
                    },
                    opacity: {
                        duration: 0.3,
                        ease: "easeOut",
                    },
                },
            });

            if (cancelled) return;

            if (number != null) {
                countControls = animate(count, number, {
                    duration: 1.35,
                    delay: 0.05,
                    ease: [0.16, 1, 0.3, 1],
                });
            }

            controls.start({
                y: [0, -14, -4, -11, 0],
                rotate: [rotate, rotate + 3.2, rotate - 2.2, rotate + 1.4, rotate],
                transition: {
                    duration: 4.2 + index * 0.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.2 * index,
                },
            });
        };

        run();
        return () => {
            cancelled = true;
            controls.stop();
            if (countControls) countControls.stop();
        };
    }, [controls, count, delay, index, number, prefix, reduceMotion, rotate, suffix]);

    return (
        <motion.div
            className={styles.banner_rotate_item}
            style={{
                background,
                marginLeft: `${left}px`,
            }}
            initial={
                reduceMotion
                    ? false
                    : {
                        y: -620 - index * 40,
                        x: startX,
                        rotate: spin,
                        opacity: 0,
                        scale: 0.4,
                    }
            }
            animate={controls}
            whileHover={
                reduceMotion
                    ? undefined
                    : {
                        y: -18,
                        scale: 1.05,
                        transition: { type: "spring", stiffness: 280, damping: 16 },
                    }
            }
        >
            <h2 style={{ color: valueColor }}>
                {displayValue}
            </h2>
            <span style={{ color: textColor }}>
                {text}
            </span>
        </motion.div>
    );
}
